import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export function SocketProvider({ children, connect }) {
  const { accessToken, user } = useAuth();
  const accessTokenRef = useRef(accessToken);
  const socketRef      = useRef(null);   // source of truth for the live socket
  const [socket, setSocket]       = useState(null);
  const [connected, setConnected] = useState(false);

  // Keep the ref current so the socket always sends the latest token on reconnect
  // without tearing down the connection on every silent token refresh.
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  // Only create/destroy the socket when the user identity changes (login/logout).
  // Using user?.id (primitive) means token refreshes never trigger a reconnect.
  const userId = user?.id ?? null;
  useEffect(() => {
    // ── Teardown path ─────────────────────────────────────────────────────────
    if (!connect || !userId || !accessTokenRef.current) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    // ── Guard: don't open a second socket for the same user ───────────────────
    // React StrictMode mounts effects twice in dev; this prevents a duplicate
    // connection from slipping through before the first cleanup fires.
    if (socketRef.current) return;

    // ── Create socket ─────────────────────────────────────────────────────────
    const SERVER_URL = process.env.REACT_APP_API_URL || '';
    const newSocket = io(SERVER_URL, {
      auth: (cb) => cb({ token: accessTokenRef.current }),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect',    () => setConnected(true));
    newSocket.on('disconnect', () => setConnected(false));

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnected(false);
    };
  }, [connect, userId]); // eslint-disable-line

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
export default SocketContext;
